// eslint-disable complexity
import {trackEvent} from '@hconnect/common/logging/Analytics'
import {QuantityRange} from '@hconnect/tuc-processes/fields'
import {useQuantity} from '@hconnect/tuc-processes/fields/quantity/useQuantity'
import {useOrderProcessFormContext} from '@hconnect/tuc-processes/form/useOrderProcessFormContext'
import {Alert, ControlledSwitch} from '@hm/fundamental'
import {Box, Typography, Collapse} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {useRmcCountryFeatures} from '../../../hooks/useRmcCountryFeatures'

import {AutoCorrection} from './AutoCorrection.types'
import {FullLoadsSection} from './FullLoadsSection'
import {LoadBalanceSection} from './LoadBalanceSection'
import {PlusLoadToggle} from './PlusLoadToggle'
import {QuantityInput} from './QuantityInput'
import {TruckTypeSelect} from './TruckTypeSelect'
import {useQuantityCorrection} from './useQuantityCorrection'

export type {AutoCorrection}

export interface QuantityStepProps {
  /**
   * Optional range configuration to override the default quantity configuration.
   * If provided, this will be used instead of the default configuration from useQuantity hook.
   */
  range?: QuantityRange

  /**
   * Optional banner text to display above the quantity input.
   * Can be used for important notifications or instructions.
   */
  bannerText?: string

  /**
   * Optional hint text to display next to the quantity input.
   * Can be used for additional guidance or information.
   */
  hintText?: string

  /**
   * Visual state of the quantity input (similar to ControlledInput state).
   * - 'error': Shows error styling (red border)
   * - 'warning': Shows warning styling (yellow/orange border)
   * - undefined: Shows default styling
   */
  state?: 'error' | 'warning'

  /**
   * Flag to enable/disable the truck type selector feature.
   * When false, truck selector is hidden regardless of region features.
   * When true (default), region features are checked to determine visibility.
   *
   * TEMPORARY: After fully switching to v2 options, we should have a pre-calculated
   * flag available in configuration. Right now we are still using onsite static data
   * that is shared between order intake and order management.
   */
  enableTruckSelector?: boolean
}

/**
 * Second step of the RMC order stepper.
 * Allows the user to enter the quantity of RMC material they want to order.
 * Supports two modes: exact quantity (m³) and full truck loads (1-3).
 */
export const QuantityStep: React.FC<QuantityStepProps> = ({
  range: rangeOverride,
  bannerText,
  hintText,
  state,
  enableTruckSelector = true
}) => {
  const {t} = useTranslation()

  // Use the form context to get validation errors
  const {formState} = useOrderProcessFormContext()

  // Get region features for conditional rendering
  const regionFeatures = useRmcCountryFeatures()

  // Use the specialized hooks from orderProcessesPackage
  const quantityReturn = useQuantity()
  const {quantityConfiguration, setType, isFullLoadEnabled} = quantityReturn

  // Use override range if provided, otherwise use default configuration
  const range = rangeOverride ?? quantityConfiguration
  const {min: minValue, max: maxValue, step, uom: quantityUom} = range

  // Get validation errors for all quantity-related fields
  const quantityError = (formState.errors?.quantity as {message?: string} | undefined)?.message
  const plusLoadError = formState.errors?.plusLoadExpected?.message
  const truckTypeError = formState.errors?.truckType?.message

  // Use custom hook for auto-correction management (only relevant in quantity mode)
  const {correctionMessage, hasCorrection, handleAutoCorrect} = useQuantityCorrection({
    minValue,
    maxValue,
    step,
    quantityUom
  })

  // Handle quantity change with analytics tracking (only used in quantity mode)
  const handleQuantityChange = React.useCallback(
    async (newQuantity: number) => {
      if (quantityReturn.type !== 'volume') return
      trackEvent('rmcQuantityChanged', {
        quantity: newQuantity,
        unit: quantityUom,
        businessLine: 'RMC'
      })

      await quantityReturn.setVolume(newQuantity)
    },
    [quantityReturn, quantityUom]
  )

  // Handle mode switch via ControlledSwitch
  const handleModeChange = React.useCallback(
    (value: string | number) => {
      const mode = value as 'volume' | 'fullLoads'
      trackEvent('rmcQuantityModeChanged', {
        mode,
        businessLine: 'RMC'
      })
      setType(mode)
    },
    [setType]
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
        padding: 0
      }}
    >
      {/* Banner Text - display important notifications */}
      {bannerText && (
        <Alert variant="warning" data-test-id="quantity-banner">
          {bannerText}
        </Alert>
      )}

      {/* Mode Toggle - only shown when full loads feature is enabled */}
      {isFullLoadEnabled && (
        <Box
          sx={{
            '& > div': {width: '100%'},
            '& > div > button': {flex: 1}
          }}
        >
          <ControlledSwitch
            value={quantityReturn.type}
            options={[
              {
                label: t('orderIntake.rmc.steps.quantity.byQuantity', 'By quantity'),
                value: 'volume'
              },
              {
                label: t('orderIntake.rmc.steps.quantity.byFullLoads', 'By full loads'),
                value: 'fullLoads'
              }
            ]}
            onChange={handleModeChange}
            data-cy="quantity-mode-switch"
          />
        </Box>
      )}

      {/* Quantity Details */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '48px', // 12 * 4px = 48px from design
          alignItems: 'center'
        }}
      >
        {/* Quantity Input Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px', // 4 * 4px = 16px from design
            width: '100%'
          }}
        >
          {/* Mode-specific content */}
          {quantityReturn.type === 'volume' ? (
            <>
              <QuantityInput
                value={quantityReturn.volume}
                onChange={handleQuantityChange}
                range={range}
                onAutoCorrect={handleAutoCorrect}
                state={state}
                hintText={hintText}
                data-test-id="rmc-quantity-input"
              />

              {/* Quantity validation error display */}
              {quantityError && (
                <Box sx={{mt: 1}}>
                  <Typography color="error" variant="body2">
                    {quantityError}
                  </Typography>
                </Box>
              )}

              {/* Correction notification - show when an auto-correction happens */}
              <Collapse in={hasCorrection}>
                <Alert variant="info" data-test-id="quantity-correction-alert">
                  {correctionMessage}
                </Alert>
              </Collapse>
            </>
          ) : (
            <>
              <FullLoadsSection quantityReturn={quantityReturn} />

              {/* Full loads validation error display */}
              {quantityError && (
                <Box sx={{mt: 1}}>
                  <Typography color="error" variant="body2">
                    {quantityError}
                  </Typography>
                </Box>
              )}
            </>
          )}

          {/* Plus Load Toggle - shown in both modes */}
          <Box sx={{height: '40px'}}>
            <PlusLoadToggle />
          </Box>

          {/* Plus Load validation error display */}
          {plusLoadError && (
            <Box sx={{mt: 1}}>
              <Typography color="error" variant="body2">
                {plusLoadError}
              </Typography>
            </Box>
          )}

          {/* Balance Load Section - only in quantity mode */}
          {quantityReturn.type === 'volume' && <LoadBalanceSection />}
        </Box>

        {/* Truck Type Selection - only in quantity mode */}
        {quantityReturn.type === 'volume' &&
          enableTruckSelector &&
          regionFeatures.hasTruckTypeSelector && (
            <Box sx={{width: '100%'}}>
              <TruckTypeSelect />

              {/* Truck Type validation error display */}
              {truckTypeError && (
                <Box sx={{mt: 1}}>
                  <Typography color="error" variant="body2">
                    {truckTypeError}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
      </Box>
    </Box>
  )
}
